import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories, siteInfo } from '@/mocks/appData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const articleId = Number(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = useMemo(() => articles.find((a) => a.id === articleId), [articleId]);
  const articleCategory = useMemo(
    () => (article ? categories.find((c) => c.id === article.category) : null),
    [article],
  );
  const relatedArticles = useMemo(
    () =>
      article
        ? article.related
            .map((id) => articles.find((a) => a.id === id))
            .filter(Boolean) as typeof articles
        : [],
    [article],
  );

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-24 pb-16 px-4 md:px-6 bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-800 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被移除或链接失效</p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章中心
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    url: `https://cpp-official.com/article/${article.id}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://cpp-official.com/article/${article.id}`,
    },
  };

  const formatContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return (
          <h2 key={i} className="font-heading text-xl font-bold text-foreground-900 mt-10 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      }
      if (line.startsWith('### ')) {
        return (
          <h3 key={i} className="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return (
          <p key={i} className="text-sm font-semibold text-foreground-800 my-2">
            {line.replace(/\*\*/g, '')}
          </p>
        );
      }
      if (line.startsWith('- ')) {
        return (
          <li key={i} className="text-sm text-foreground-700 ml-4 mb-1 list-disc">
            {line.replace('- ', '')}
          </li>
        );
      }
      if (line.trim() === '') {
        return <div key={i} className="h-3"></div>;
      }
      return (
        <p key={i} className="text-sm text-foreground-700 leading-relaxed">
          {line}
        </p>
      );
    });
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <ScrollToTop />
      <main>
        <Navbar />
        <article className="pt-24 pb-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[800px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '文章中心', to: '/category/all' },
                { label: articleCategory?.name || '', to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-2.5 py-1 text-xs font-medium whitespace-nowrap rounded-full bg-primary-100 text-primary-700">
                  {articleCategory?.name || article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500">·</span>
                <span className="text-xs text-foreground-500">{article.readTime} 阅读</span>
                <span className="text-xs text-foreground-500">·</span>
                <span className="text-xs text-foreground-500">{article.author}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.cover}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="prose-custom">
              {formatContent(article.content)}
            </div>

            <div className="mt-8 flex items-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs whitespace-nowrap rounded-full bg-background-100 text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200/70">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={ra.cover}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-2">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
        <Footer />
      </main>
    </>
  );
}